import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { MsalProvider, AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import { msalConfig, loginRequest } from './config/authConfig';
import { AdminLayout } from './components/layout/AdminLayout';
import { DashboardPage } from './pages/DashboardPage';
import { SubscriptionsPage } from './pages/SubscriptionsPage';
import { StudentsPage } from './pages/StudentsPage';
import { SchoolsPage } from './pages/SchoolsPage';
import { LeadsPage } from './pages/LeadsPage';
import { CampaignsPage } from './pages/CampaignsPage';
import { CampaignEditorPage } from './pages/CampaignEditorPage';
import { SurveysPage } from './pages/SurveysPage';
import { ContentPage } from './pages/ContentPage';
import { ContentBatchPage } from './pages/ContentBatchPage';
import LoadingSpinner from './components/common/LoadingSpinner';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { ThemeToggle } from './components/common/ThemeToggle';
import { adminApi } from './services/adminApi';

// Initialize MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);

function AppContent() {
  const { instance, accounts, inProgress } = useMsal();
  const [loading, setLoading] = useState(true);
  const [msalInitialized, setMsalInitialized] = useState(false);

  // Wait for MSAL to initialize
  useEffect(() => {
    const initializeMsal = async () => {
      try {
        await instance.initialize();
        setMsalInitialized(true);
      } catch (err) {
        console.log('[MSAL] Already initialized or initialization failed:', err);
        setMsalInitialized(true);
      }
    };
    initializeMsal();
  }, [instance]);

  // Set up token provider for API calls
  useEffect(() => {
    if (msalInitialized && accounts.length > 0) {
      adminApi.setTokenProvider(async () => {
        const account = accounts[0];
        try {
          const response = await instance.acquireTokenSilent({
            ...loginRequest,
            account,
          });
          return response.idToken;
        } catch (error) {
          // If silent acquisition fails, try popup
          const response = await instance.acquireTokenPopup({
            ...loginRequest,
            account,
          });
          return response.idToken;
        }
      });
      setLoading(false);
    } else if (msalInitialized && accounts.length === 0 && inProgress === 'none') {
      setLoading(false);
    }
  }, [accounts, msalInitialized, inProgress, instance]);

  const handleLogin = () => {
    instance.loginRedirect(loginRequest);
  };

  if (!msalInitialized || loading) {
    return <LoadingSpinner message="Loading..." />;
  }

  return (
    <>
      <AuthenticatedTemplate>
        <AdminLayout>
          <Routes>
            <Route path="/" element={<DashboardPage />} />
            <Route path="/subscriptions" element={<SubscriptionsPage />} />
            <Route path="/students" element={<StudentsPage />} />
            <Route path="/surveys" element={<SurveysPage />} />
            <Route path="/schools" element={<SchoolsPage />} />
            <Route path="/leads" element={<LeadsPage />} />
            <Route path="/content" element={<ContentPage />} />
            <Route path="/content/:batchId" element={<ContentBatchPage />} />
            <Route path="/campaigns" element={<CampaignsPage />} />
            <Route path="/campaigns/new" element={<CampaignEditorPage />} />
            <Route path="/campaigns/:id/edit" element={<CampaignEditorPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AdminLayout>
      </AuthenticatedTemplate>

      <UnauthenticatedTemplate>
        <div className="min-h-screen bg-background flex flex-col">
          {/* Header */}
          <header className="w-full py-4 px-6 bg-card shadow-xs border-b border-border">
            <div className="max-w-6xl mx-auto flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img
                  src="https://www.bankvaultacademy.org/images/logo.png"
                  alt="Bank Vault Academy"
                  className="h-10 w-auto"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
                <span className="text-xl font-semibold text-primary">
                  Bank Vault Academy
                </span>
              </div>
              <ThemeToggle />
            </div>
          </header>

          {/* Main content */}
          <main className="flex-1 flex items-center justify-center p-6">
            <div className="max-w-md w-full">
              <div className="bg-card rounded-xl shadow-lg p-8 border border-border">
                <div className="text-center mb-8">
                  <h1 className="text-3xl font-bold text-primary mb-2">
                    Admin Portal
                  </h1>
                  <p className="text-muted-foreground">
                    Internal management dashboard
                  </p>
                </div>

                <div className="space-y-4">
                  <button
                    onClick={handleLogin}
                    className="w-full py-3 px-6 rounded-lg font-medium text-white text-lg
                               bg-primary hover:bg-primary/90
                               transition-colors duration-200 shadow-md hover:shadow-lg"
                  >
                    Sign In with Microsoft
                  </button>
                </div>
              </div>
            </div>
          </main>

          {/* Footer */}
          <footer className="py-4 text-center text-sm text-muted-foreground">
            <p>&copy; {new Date().getFullYear()} Bank Vault Academy. All rights reserved.</p>
          </footer>
        </div>
      </UnauthenticatedTemplate>
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <MsalProvider instance={msalInstance}>
        <Router>
          <AppContent />
        </Router>
      </MsalProvider>
    </ErrorBoundary>
  );
}

export default App;
